import React from 'react';
import { Alert, Button, Card, CardBody } from '@edgebox/react-components';
import { JsonView, collapseAllNested } from 'react-json-view-lite';

import { jsonStyle } from '../../../../Helpers';
import type { EvaluateFormulaResponse, FormulaErrorDto, ValidateFormulaResponse } from '@edgebox/sync-core-rest-client/dist/services/api';

function formatReturnType(returnType: unknown): string {
  if (typeof returnType === 'string') return returnType;
  try {
    return JSON.stringify(returnType);
  } catch {
    return String(returnType);
  }
}

export function FormulaResultTab(props: {
  validateResult?: ValidateFormulaResponse;
  evaluateResult?: EvaluateFormulaResponse;
  onSelectSpan: (err: FormulaErrorDto) => void;
}) {
  const { validateResult, evaluateResult, onSelectSpan } = props;

  return (
    <Card className="mt-2">
      <CardBody>
        {validateResult ? (
          <>
            <div className="small">
              <div>
                <span className="fw-bold">Valid:</span> {validateResult.ok ? 'yes' : 'no'}
              </div>
              <div>
                <span className="fw-bold">Return type:</span> <code>{formatReturnType(validateResult.returnType)}</code>
              </div>
              <div>
                <span className="fw-bold">Static:</span> {validateResult._static ? 'yes' : 'no'}
              </div>
            </div>

            {validateResult.errors?.length ? (
              <div className="mt-3">
                <div className="fw-bold">Errors</div>
                <div className="mt-2 d-flex flex-column gap-2">
                  {validateResult.errors.map((e, idx) => (
                    <Button key={idx} variant="danger" size="sm" className="text-start" onClick={() => onSelectSpan(e)}>
                      <div className="fw-bold">
                        {e.code} <span className="fw-normal">({e.kind})</span>
                      </div>
                      <div className="small">{e.message}</div>
                      {e.span ? (
                        <div className="small text-muted">
                          span: {e.span.start}..{e.span.end}
                        </div>
                      ) : null}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <Alert variant="success" className="mt-3 mb-0">
                No validation errors.
              </Alert>
            )}

            <div className="mt-3">
              <div className="fw-bold">Requirements</div>
              <div className="mt-2">
                <JsonView data={validateResult.requirements} style={jsonStyle} shouldExpandNode={collapseAllNested} />
              </div>
            </div>

            <div className="mt-3">
              <div className="fw-bold">AST</div>
              <div className="mt-2">
                <JsonView data={validateResult.ast} style={jsonStyle} shouldExpandNode={collapseAllNested} />
              </div>
            </div>
          </>
        ) : (
          <Alert variant="info" className="mb-0">
            Start typing to validate.
          </Alert>
        )}

        {evaluateResult ? (
          <>
            <div className="mt-3">
              <div className="fw-bold">Value</div>
              <div className="mt-2">
                <JsonView data={evaluateResult.value} style={jsonStyle} shouldExpandNode={collapseAllNested} />
              </div>
            </div>
            <div className="mt-3">
              <div className="fw-bold">Execution output</div>
              <div className="mt-2">
                <JsonView data={evaluateResult.output} style={jsonStyle} shouldExpandNode={collapseAllNested} />
              </div>
            </div>
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}
